import React, { useEffect, useState, useRef } from 'react';
import '../Styles/LeftSidebar.css';
import { useNoteSelection } from '../context/NoteSelectionContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaHome, FaComments, FaCrown, FaUserEdit, FaSignOutAlt, FaAngleLeft, FaAngleRight, FaTimes, FaSpinner } from 'react-icons/fa';
import routesfe from '../Config/RoutesFE';
import configDetails from '../Config/Config';

const { trackingUrl, GET_TRACKING_TIME } = configDetails;

function LeftSidebar({ onHomeClick, isOpen, onClose, isMiniSidebar, onToggleMini, authIdToken }) {
  const { selectedNote, triggerHomeReset } = useNoteSelection();
  const location = useLocation();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileBtnRef = useRef(null);
  const profileMenuRef = useRef(null);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  // Tracking time state
  const [minutesUsed, setMinutesUsed] = useState(0);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [daysLeft, setDaysLeft] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [planName, setPlanName] = useState('');
  const [planEndDate, setPlanEndDate] = useState('');
  const [loading, setLoading] = useState(true); // Add loading state

  // Fetch tracking time from backend
  const fetchTrackingTime = async () => {
    setLoading(true); // Set loading to true before fetching
    try {
      const storedUser = JSON.parse(localStorage.getItem('user'));
      // Now using cognito_user_id instead of userSub
      const id = storedUser?.cognito_user_id || storedUser?.userSub; // Fall back to userSub for backward compatibility
      if (!id) {
        setLoading(false);
        return;
      }
      const res = await fetch(`${trackingUrl}${GET_TRACKING_TIME}?user_id=${id}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authIdToken
        }
      });
      if (!res.ok) throw new Error('Failed to fetch tracking time');
      const data = await res.json();
      setMinutesUsed(data.used_minutes || 0);
      setTotalMinutes(data.total_minutes || 0);
      setDaysLeft(data.days_left || 0);
      setSubscriptionStatus(data.subscription_status_detail?.status || '');
      setPlanName(data.subscription_status_detail?.plan_name || '');
      setPlanEndDate(data.subscription_status_detail?.end_date || '');
      setLoading(false); // Set loading to false after data is fetched
    } catch (err) {
      setMinutesUsed(0);
      setTotalMinutes(0);
      setDaysLeft(0);
      setSubscriptionStatus('');
      setPlanName('');
      setPlanEndDate('');
      setLoading(false); // Set loading to false in case of error
    }
  };

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUserName(storedUser.name);
      setUserEmail(storedUser.email);
    }
    }, []);

  useEffect(() => {
    if (!authIdToken) return;
    fetchTrackingTime();

    // Listen for custom event to refresh tracking time
    const handler = () => fetchTrackingTime();
    window.addEventListener('refresh-tracking-time', handler);
    return () => window.removeEventListener('refresh-tracking-time', handler);
  }, [authIdToken]);

  const handleProfileClick = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const toggleSidebar = () => {
    if (onToggleMini) {
      onToggleMini();
    }
    setShowProfileMenu(false);
  };

  useEffect(() => {
    if (!showProfileMenu) return;

    function handleClickOutside(e) {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target) &&
        profileBtnRef.current &&
        !profileBtnRef.current.contains(e.target)
      ) {
        setShowProfileMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  // Close mini dropdown when collapsing back to full sidebar
  useEffect(() => {
    if (!isMiniSidebar) setShowProfileMenu(false);
  }, [isMiniSidebar]);

  const isMinutesExhausted = totalMinutes > 0 && minutesUsed >= totalMinutes;
  const isSubscriptionExpired = subscriptionStatus === 'expired' || subscriptionStatus === 'completed';
  const planNameLower = planName.toLowerCase();
  const isBasicPlan = planNameLower === 'basic' || planNameLower.startsWith('basic ');
  const isProfessionalPlan = planNameLower === 'professional' || planNameLower.startsWith('professional ');
  const isFreeTrialPlan = planNameLower === 'free trial';
  const hasNoSubscription = !planName || planName.trim() === '';
  const shouldShowUpgrade = !loading && (isBasicPlan || isProfessionalPlan || isFreeTrialPlan || isMinutesExhausted || isSubscriptionExpired);
  const shouldShowSubscribe = !loading && hasNoSubscription;

  const isActive = (path) => {
    if (path === routesfe.HOME) {
      // Home is only active if we're on the home route AND no note is selected
      return location.pathname === path && !selectedNote;
    }
    return location.pathname === path;
  };

  return (
    <>
      {/* Mobile Overlay - Dark background when sidebar is open on mobile */}
      {isOpen && (
        <div className="mobile-overlay" onClick={onClose} />
      )}

      <div
        className={`bg-white border-end vh-100 d-flex flex-column justify-content-between position-fixed transition-all${isMiniSidebar ? ' mini-sidebar' : ''} ${isOpen ? 'mobile-open' : ''}`}
      >
        {/* Mobile Close Button */}
        <button
          className="mobile-close-btn btn btn-sm position-absolute"
          onClick={onClose}
        >
          <FaTimes />
        </button>

        <div>
          <div className="p-3 border-bottom">
            {isMiniSidebar && (
              <div className="mini-logo-toggle-wrap text-center mb-2">
                <span
                  className="fw-bold text-primary fs-5 mini-logo-nf"
                  onClick={() => {
                    navigate(routesfe.HOME);
                    triggerHomeReset();
                    if (onHomeClick) onHomeClick();
                    if (onClose) onClose();
                  }}
                >
                  NF
                </span>
                <button
                  className="btn btn-sm btn-outline-primary mini-logo-toggle-btn desktop-toggle-btn"
                  onClick={toggleSidebar}
                  title="Open Navigation"
                >
                  <FaAngleRight />
                </button>
              </div>
            )}
            {!isMiniSidebar && (
              <div className="d-flex align-items-center justify-content-between full-logo-row">
                <h4
                  className="text-2xl font-bold text-blue-700 cursor-pointer sidebar-logo mb-0"
                  onClick={() => {
                    navigate(routesfe.HOME);
                    triggerHomeReset();
                    if (onHomeClick) onHomeClick();
                    if (onClose) onClose();
                  }}
                >
                  NoteForge
                </h4>
                <button
                  className="btn btn-sm btn-outline-primary desktop-toggle-btn full-logo-toggle-btn"
                  onClick={toggleSidebar}
                  title="Close Navigation"
                >
                  <FaAngleLeft />
                </button>
              </div>
            )}

            {/* Profile Dropdown */}
            <div className="dropdown mt-3 position-relative">
              <button
                className={isMiniSidebar
                  ? "profile-btn-mini d-flex align-items-center justify-content-center"
                  : "btn btn-light dropdown-toggle w-100 text-start d-flex align-items-center profile-btn"
                }
                ref={profileBtnRef}
                onClick={handleProfileClick}
                tabIndex={0}
              >
                <div
                  className="bg-primary text-white d-flex align-items-center justify-content-center profile-avatar"
                >
                  {userName ? userName.charAt(0).toUpperCase() : 'U'}
                </div>

                {!isMiniSidebar && (
                  <div className="flex-grow-1 overflow-hidden">
                    <div className="fw-bold">{userName}</div>
                    <div className="text-muted small profile-email">
                      {userEmail}
                    </div>
                  </div>
                )}
              </button>

              {/* Dropdown menu — full-width inside expanded sidebar, fixed beside the rail in mini mode (Otter style) */}
              <ul
                ref={profileMenuRef}
                className={`dropdown-menu p-2 ${showProfileMenu ? 'show' : ''} ${isMiniSidebar ? '' : 'w-100'}`}
                tabIndex={-1}
              >
                {isMiniSidebar && (
                  <>
                    <li className="px-2 py-2 d-flex align-items-center profile-dropdown-header">
                      <div
                        className="bg-primary text-white d-flex align-items-center justify-content-center profile-avatar me-2 flex-shrink-0"
                      >
                        {userName ? userName.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="overflow-hidden">
                        <div className="fw-bold text-dark profile-dropdown-name">{userName}</div>
                        <div className="text-muted small profile-dropdown-email">{userEmail}</div>
                      </div>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                  </>
                )}
                <li>
                  <Link className="dropdown-item d-flex align-items-center" to={routesfe.PROFILE} onClick={() => { setShowProfileMenu(false); if (onClose) onClose(); }}>
                    <FaUserEdit className="me-2 text-secondary" /> Edit Profile
                  </Link>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button className="dropdown-item d-flex align-items-center" onClick={() => {
                    [
                      'outlook_code_verifier',
                      'pending_provider_switch',
                      'pre_switch_google_token', 'pre_switch_google_refresh_token',
                      'accessToken', 'calendarEvents',
                    ].forEach(key => localStorage.removeItem(key));
                    setShowProfileMenu(false);
                    if (onClose) onClose();
                    navigate(routesfe.LOGOUT);
                  }}>
                    <FaSignOutAlt className="me-2" /> Logout
                  </button>
                </li>
              </ul>
            </div>

          </div>

          {/* Navigation */}
          <ul className="nav flex-column p-3">
            <li className="nav-item mb-2">
              <Link
                to={routesfe.HOME}
                className={`nav-link d-flex align-items-center ${isActive(routesfe.HOME) ? 'text-primary fw-bold' : 'text-dark'}`}
                onClick={() => {
                  triggerHomeReset();
                  if (onHomeClick) {
                    onHomeClick();
                  }
                  if (onClose) onClose();
                }}
              >
                <FaHome className="me-2" />
                {!isMiniSidebar && 'Home'}
              </Link>
            </li>
            {/* NoteForge AI Chat - temporarily disabled
            <li className="nav-item mb-2">
              <Link
                to={routesfe.CHAT}
                className={`nav-link d-flex align-items-center ${isActive(routesfe.CHAT) ? 'text-primary fw-bold' : 'text-dark'}`}
                onClick={() => { if (onClose) onClose(); }}
              >
                <FaComments className="me-2" />
                {!isMiniSidebar && 'NoteForge AI Chat'}
              </Link>
            </li>
            */}
          </ul>
        </div>

        <div className="border-top p-3">
          {!isMiniSidebar && (
            <>
              <p className="mb-1 small text-muted">{planName || 'Plan'}</p>
              {loading ? (
                <div className="d-flex align-items-center justify-content-center py-3">
                  <FaSpinner className="text-primary me-2" style={{ animation: 'spin 1s linear infinite' }} size={18} />
                  <small className="text-muted">Loading...</small>
                </div>
              ) : (
                <>
                  <div className="progress progress-minutes">
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{ width: `${totalMinutes > 0 ? (minutesUsed / totalMinutes) * 100 : 0}%` }}
                    />
                  </div>
                  <small className="text-muted">
                    {minutesUsed} of {totalMinutes} monthly minutes used.<br />
                    {planEndDate
                      ? `Minutes reset on ${new Date(planEndDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`
                      : `Minutes reset in ${daysLeft} days`
                    }<br />
                  </small>
                </>
              )}
              {shouldShowSubscribe && (
                <Link to={routesfe.PLANS} className="btn btn-primary btn-sm w-100 mt-2" onClick={() => { if (onClose) onClose(); }}>
                  Subscribe Now
                </Link>
              )}
              {shouldShowUpgrade && (
                <Link to={routesfe.UPGRADE} className="btn btn-outline-primary btn-sm w-100 mt-2" onClick={() => { if (onClose) onClose(); }}>
                  Upgrade Plan
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default LeftSidebar;